import { AbstractMesh, BoundingBox, Camera, Mesh, MeshBuilder, Ray, Scene, Vector3 } from '@babylonjs/core'
import ModelViewer from '@lindberg/modelviewer'
import getMaterial from '../MaterialLoader'

const closeEnough = (value: number, threshold = 0.001) => Math.abs(value) < threshold

function rayIntersectsAABB(ray: Ray, min: Vector3, max: Vector3): Vector3 | null {
	let tmin = (min.x - ray.origin.x) / ray.direction.x
	let tmax = (max.x - ray.origin.x) / ray.direction.x
	if (tmin > tmax) [tmin, tmax] = [tmax, tmin]

	let tymin = (min.y - ray.origin.y) / ray.direction.y
	let tymax = (max.y - ray.origin.y) / ray.direction.y
	if (tymin > tymax) [tymin, tymax] = [tymax, tymin]

	if ((tmin > tymax) || (tymin > tmax)) return null
	if (tymin > tmin) tmin = tymin
	if (tymax < tmax) tmax = tymax

	let tzmin = (min.z - ray.origin.z) / ray.direction.z
	let tzmax = (max.z - ray.origin.z) / ray.direction.z
	if (tzmin > tzmax) [tzmin, tzmax] = [tzmax, tzmin]

	if ((tmin > tzmax) || (tzmin > tmax)) return null
	if (tzmin > tmin) tmin = tzmin
	if (tzmax < tmax) tmax = tzmax

	// Compute the intersection point
	const distance = tmin >= 0 ? tmin : tmax
	if (distance < 0) return null

	return ray.origin.add(ray.direction.scale(distance))
}
let modelViewer: ModelViewer
let bounds: BoundingBox
let min: Vector3
let max: Vector3
let nosepadsCenter: Vector3

export function UpdateRayIndex(viewer: ModelViewer, enabled = true) {
	modelViewer = viewer
	modelViewer.scene.meshes.forEach(x => x.alphaIndex = 0)

	bounds = getCombinedBoundingBox(modelViewer.scene.meshes.filter(x => x.name.startsWith('LENS')))
	// bounds = modelViewer.scene.meshes.find(x => x.name == 'FRONT')?.getBoundingInfo().boundingBox
	if (!bounds) enabled = false

	if (enabled) {
		min = bounds.minimumWorld
		max = bounds.maximumWorld
		nosepadsCenter = modelViewer.frame.parts.nosepads.map(x => x.absolutePosition).reduce((a, b) => a.add(b)).scale(1 / modelViewer.frame.parts.nosepads.length)
		modelViewer.camera.onViewMatrixChangedObservable.removeCallback(onViewMatrixChanged)

		modelViewer.camera.onViewMatrixChangedObservable.add(onViewMatrixChanged)
		onViewMatrixChanged()
	} else {
		modelViewer.camera.onViewMatrixChangedObservable.removeCallback(onViewMatrixChanged)
	}
}
function getCombinedBoundingBox(meshes: AbstractMesh[]): BoundingBox | null {
	if (!meshes || meshes.length === 0) {
		return null
	}

	let combinedMin: Vector3 | null = null
	let combinedMax: Vector3 | null = null

	for (const mesh of meshes) {
		if (!mesh) continue

		mesh.computeWorldMatrix(true)
		const boundingBox = mesh.getBoundingInfo().boundingBox
		const min = boundingBox.minimumWorld
		const max = boundingBox.maximumWorld

		if (!combinedMin) {
			combinedMin = min.clone()
			combinedMax = max.clone()
		} else {
			combinedMin = Vector3.Minimize(combinedMin, min)
			combinedMax = Vector3.Maximize(combinedMax, max)
		}
	}

	if (!combinedMin || !combinedMax) return null

	return new BoundingBox(combinedMin, combinedMax)
}

function onViewMatrixChanged() {
	const origin = modelViewer.camera.position.clone()
	const direction = nosepadsCenter.subtract(origin).normalize()
	const ray = new Ray(origin, direction)

	const hitPoint = rayIntersectsAABB(ray, min, max)
	if (hitPoint) {
		let side: string = 'unknown'
		if (closeEnough(hitPoint.z - max.z)) side = 'front'
		else if (closeEnough(hitPoint.z - min.z)) side = 'back'
		else if (closeEnough(hitPoint.x - max.x)) side = 'left'
		else if (closeEnough(hitPoint.x - min.x)) side = 'right'
		else if (closeEnough(hitPoint.y - max.y)) side = 'top'
		else if (closeEnough(hitPoint.y - min.y)) side = 'bottom'
		if (side === 'unknown') return

		modelViewer.scene.meshes.forEach(x => x.alphaIndex = 0)
		if (side === 'back') {
			modelViewer.frame.parts.nosepads.forEach(x => x.meshes.forEach(m => m.alphaIndex = 1))
		} else {
			modelViewer.frame.parts.nosepads.forEach(x => x.meshes.forEach(m => m.alphaIndex = -1))
		}
	}
}
